/**
 * WebGPU adapter + device acquisition with explicit capability checks.
 *
 * The plan requires no silent failures: every failure path raises a
 * structured error that the caller surfaces in the boot overlay.
 */

export type Capabilities = {
  rgba16floatFilterable: boolean;
  feedbackFormat: GPUTextureFormat;
};

export type GpuContext = {
  adapter: GPUAdapter;
  device: GPUDevice;
  capabilities: Capabilities;
};

export class WebGpuUnavailableError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'WebGpuUnavailableError';
  }
}

export async function acquireDevice(): Promise<GpuContext> {
  if (!('gpu' in navigator) || !navigator.gpu) {
    throw new WebGpuUnavailableError(
      'navigator.gpu is undefined. Use a WebGPU-capable browser (latest Chrome or Edge on desktop).',
    );
  }

  let adapter: GPUAdapter | null;
  try {
    adapter = await navigator.gpu.requestAdapter({ powerPreference: 'high-performance' });
  } catch (err) {
    throw new WebGpuUnavailableError(
      `requestAdapter() threw: ${err instanceof Error ? err.message : String(err)}`,
    );
  }
  if (!adapter) {
    throw new WebGpuUnavailableError(
      'No suitable WebGPU adapter. Your GPU/driver may not be supported, or WebGPU is disabled.',
    );
  }

  // float32-filterable is the texture-feature flag that lets `linear` samplers
  // filter rgba16float / rgba32float textures. Many browsers still gate it.
  // We still try to create the device with it; if unavailable we fall back to
  // unfilterable + a manual fetch path inside the warp shader, but for Phase 0
  // we keep things simple and just degrade to rgba8unorm for the feedback ring.
  const wantsFloat16Filterable = adapter.features.has('float32-filterable');
  const requiredFeatures: GPUFeatureName[] = [];
  if (wantsFloat16Filterable) requiredFeatures.push('float32-filterable');

  let device: GPUDevice;
  try {
    device = await adapter.requestDevice({ requiredFeatures });
  } catch (err) {
    throw new WebGpuUnavailableError(
      `requestDevice() failed: ${err instanceof Error ? err.message : String(err)}`,
    );
  }

  device.lost.then((info) => {
    console.error('[gpu] device lost:', info.reason, info.message);
  });

  device.addEventListener('uncapturederror', (event) => {
    const e = event as GPUUncapturedErrorEvent;
    console.error('[gpu] uncaptured error:', e.error.message);
  });

  // Probe rgba16float as a render-target + filterable sample target. If the
  // browser refuses to filter it we drop to rgba8unorm for the feedback ring.
  const rgba16floatFilterable = wantsFloat16Filterable;
  const feedbackFormat: GPUTextureFormat = rgba16floatFilterable
    ? 'rgba16float'
    : 'rgba8unorm';

  return {
    adapter,
    device,
    capabilities: {
      rgba16floatFilterable,
      feedbackFormat,
    },
  };
}

/**
 * Wraps async pipeline / resource creation in a validation error scope.
 * Logs nothing on success; surfaces a single readable error otherwise.
 */
export async function withValidationScope<T>(
  device: GPUDevice,
  label: string,
  fn: () => T | Promise<T>,
): Promise<T> {
  device.pushErrorScope('validation');
  let result: T;
  try {
    result = await fn();
  } catch (err) {
    device.popErrorScope().catch(() => {});
    throw err;
  }
  const validationError = await device.popErrorScope();
  if (validationError) {
    throw new Error(`[${label}] WebGPU validation: ${validationError.message}`);
  }
  return result;
}
